import { useMemo, useState } from "react";

import { Input } from "../ui/input";
import { goalAncestry, type ManagedGoal } from "./goalModel";

/**
 * Choose zero or more OPEN goals (GF1: an explicit empty selection is a
 * choice, never a default to Harmony). Closed goals stay visible only while
 * already selected, so a saved focus never silently loses a member.
 */
export function GoalPicker({
  goals,
  value,
  onChange,
  disabled = false,
}: {
  readonly goals: ReadonlyArray<ManagedGoal>;
  readonly value: ReadonlyArray<string>;
  readonly onChange: (ids: string[]) => void;
  readonly disabled?: boolean;
}) {
  const [query, setQuery] = useState("");
  const byId = useMemo(() => new Map(goals.map((goal) => [goal.goalId, goal])), [goals]);
  const needle = query.toLowerCase();
  const rows = goals.filter((goal) => {
    if (goal.state !== "open" && !value.includes(goal.goalId)) return false;
    const ancestry = goalAncestry(goal, byId).join(" / ");
    return `${goal.title} ${goal.statement} ${ancestry}`.toLowerCase().includes(needle);
  });
  return (
    <div className="grid gap-2" data-goal-picker="">
      <Input
        size="sm"
        aria-label="Search goals"
        placeholder="Search goals…"
        value={query}
        onChange={(event) => setQuery(event.target.value)}
      />
      <div className="ncl-board-rows h-64 overflow-y-auto" role="group" aria-label="Select goals">
        {rows.map((goal) => {
          const ancestry = goalAncestry(goal, byId).join(" / ");
          const checked = value.includes(goal.goalId);
          return (
            <label
              key={goal.goalId}
              className="ncl-board-row cursor-pointer"
              data-goal-picker-row={goal.goalId}
              data-selected={checked ? "true" : undefined}
            >
              <input
                type="checkbox"
                className="size-4 accent-[var(--board-accent)]"
                aria-label={goal.title}
                checked={checked}
                disabled={disabled || goal.state !== "open"}
                onChange={() =>
                  onChange(
                    checked ? value.filter((id) => id !== goal.goalId) : [...value, goal.goalId],
                  )
                }
              />
              <span className="ncl-board-row-main">
                <span className="block truncate">{goal.title}</span>
                {ancestry ? (
                  <span
                    className="block truncate text-[11px] text-muted-foreground"
                    title={ancestry}
                  >
                    {ancestry}
                  </span>
                ) : null}
              </span>
              <span className="ncl-board-row-trail">{goal.state === "open" ? "" : "closed"}</span>
            </label>
          );
        })}
        {goals.length === 0 ? <p className="ncl-board-fine p-2">No goals available.</p> : null}
      </div>
      <p className="ncl-board-fine">{value.length} selected</p>
    </div>
  );
}
